import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, AlertTriangle } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CONTACT } from '../../constants/images';

export const Emergency = () => {
  const services = [
    {
      title: 'Post-Traumatic Haemorrhage — Emergency Embolization',
      description: 'Road accidents, falls, and blunt abdominal trauma cause life-threatening haemorrhage from the liver, spleen, kidneys, pelvis, or abdominal vessels. Catheter-based embolization controls many of these bleeds without emergency surgery.',
      symptoms: ['Haemodynamic instability after trauma', 'Active bleeding on contrast CT scan', 'Expanding haematoma on imaging'],
      treatments: ['Superselective Embolization under DSA', 'Embolic coils, gelfoam, or glue deployment', 'Often faster than emergency theatre setup', 'Avoids operating on unstable patients']
    },
    {
      title: 'GI Bleeding, Hemoptysis & Hematuria',
      description: 'Massive bleeding from the gastrointestinal tract, lungs, or urinary system arises from tumours, ulcers, angiodysplasia, or vascular malformations. When endoscopy fails, IR is the definitive next step.',
      symptoms: ['Massive GI haemorrhage unresponsive to medical treatment', 'Coughing blood (hemoptysis)', 'Blood in urine (hematuria)', 'Hemodynamic instability'],
      treatments: ['Angiographic localisation of bleeding vessel', 'Targeted embolization with coils, particles, or glue', 'Haemostasis achieved within minutes']
    },
    {
      title: 'Postpartum Haemorrhage (PPH) — Emergency UAE',
      description: 'Postpartum haemorrhage is a leading cause of maternal mortality. When uterotonic drugs fail, uterine artery embolization offers a life-saving, uterus-preserving alternative to emergency hysterectomy.',
      symptoms: ['Excessive bleeding after childbirth', 'Failed conservative management', 'Hemodynamic instability'],
      treatments: ['Emergency bilateral uterine artery embolization', 'Performed under local anaesthesia and sedation', 'Uterus-preserving alternative to hysterectomy', 'Future pregnancies reported after PPH embolization']
    },
    {
      title: 'Aortic Aneurysm & Dissection — EVAR',
      description: 'Aortic aneurysm and aortic dissection are potentially fatal vascular emergencies. Open surgical repair carries high operative mortality in many patients.',
      symptoms: ['Severe chest, back, or abdominal pain', 'Pulsatile abdominal mass', 'Hypotension or shock'],
      treatments: ['EVAR (Endovascular Aortic Repair)', 'Stent-graft via femoral artery', 'No abdominal incision', 'Significantly lower operative mortality']
    }
  ];

  const faqs = [
    { q: 'How quickly can emergency embolization be set up in Mumbai?', a: 'Catheter-based embolization can be initiated within 30-60 minutes of decision-to-treat — often faster than setting up an emergency surgical theatre.' },
    { q: 'Can postpartum haemorrhage be treated without hysterectomy?', a: 'Yes. Emergency uterine artery embolization has a high success rate in controlling PPH while preserving the uterus. Future pregnancies have been reported after the procedure.' },
    { q: 'Is EVAR safer than open surgery for aortic aneurysm?', a: 'For most patients — especially elderly or high-risk individuals — EVAR has significantly lower operative mortality, shorter ICU stays, and faster recovery compared to open aortic surgery.' },
    { q: 'When is IR embolization preferred over surgery for trauma bleeding?', a: 'When the patient is haemodynamically stable enough to reach the angio-suite and the bleeding vessel is accessible — especially in elderly patients or those on blood thinners.' }
  ];

  return (
    <>
      <SEO 
        title="Emergency Interventional Radiology in Mumbai | Dr. Punit Mahajan"
        description="Emergency vascular embolization for trauma bleeding, GI haemorrhage, postpartum haemorrhage & aortic aneurysm in Mumbai — rapid, life-saving interventional radiology by Dr. Punit Mahajan."
      />

      <section className="bg-gradient-to-br from-red-950 via-blue-950 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">Emergency Vascular Interventions</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>Rapid, Precise Haemorrhage Control in Mumbai</p>
        </div>
      </section>

      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg mb-12">
            <h2 className="text-2xl font-bold text-red-900 mb-2">Emergency? Call Immediately</h2>
            <p className="text-slate-700 mb-4">Dr. Punit Mahajan is available on call for emergency IR procedures across Mumbai hospitals.</p>
            <a href={`tel:${CONTACT.phone}`}>
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Emergency Contact: {CONTACT.phone}
              </Button>
            </a>
          </div>

          <div className="prose max-w-none mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Uncontrolled internal bleeding — from trauma, cancer, or childbirth complications — can be fatal within minutes to hours. Interventional Radiology provides the fastest, least invasive route to haemorrhage control: guiding a catheter to the exact bleeding vessel under live imaging and sealing it from inside without a major surgical incision.
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="card-gold-hover transition-all bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Warning Signs:</h4>
                      <ul className="space-y-2">
                        {service.symptoms.map((s, idx) => (
                          <li key={idx} className="flex items-start"><CheckCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{s}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Treatment:</h4>
                      <ul className="space-y-2">
                        {service.treatments.map((t, idx) => (
                          <li key={idx} className="flex items-start"><ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{t}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-500 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-red-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">24/7 Emergency Referrals</h2>
          <p className="text-xl text-slate-300">Hospitals across Mumbai can refer emergency cases to Dr. Punit Mahajan</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call Now: {CONTACT.phone}</Button></a>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">WhatsApp</Button></a>
          </div>
        </div>
      </section>
    </>
  );
};
